<template>
  <AppLayout>
    <div class="pt-8">
      <!-- Page Header -->
      <div class="mb-8 px-8">
        <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
          <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
          Integrations
        </div>
        <h1 class="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">Third-Party Integrations</h1>
        <p class="text-xl text-slate-300 mb-8 max-w-2xl">Connect your oilfield business with the tools you use every day</p>
      </div>

      <!-- Integration Stats -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 px-8 mb-8">
        <div class="integration-stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-xl mb-4">
            <img src="/src/assets/images/icons/gear icon with neon.png" alt="Connected" class="w-6 h-6">
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Connected Apps</p>
            <p class="text-3xl font-bold text-white">{{ integrationStats.connected }}</p>
            <p class="text-sm text-emerald-400">Active integrations</p>
          </div>
        </div>

        <div class="integration-stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-cyan-500/20 rounded-xl mb-4">
            <img src="/src/assets/images/icons/ai-powered financial research tools.png" alt="Data Sync" class="w-6 h-6">
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Data Sync</p>
            <p class="text-3xl font-bold text-white">{{ integrationStats.syncCount }}</p>
            <p class="text-sm text-emerald-400">Last 24 hours</p>
          </div>
        </div>

        <div class="integration-stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-xl mb-4">
            <img src="/src/assets/images/icons/gold dollar coin.png" alt="Automation" class="w-6 h-6">
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Automations</p>
            <p class="text-3xl font-bold text-white">{{ integrationStats.automations }}</p>
            <p class="text-sm text-emerald-400">Active workflows</p>
          </div>
        </div>

        <div class="integration-stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-slate-500/20 rounded-xl mb-4">
            <img src="/src/assets/images/icons/books.png" alt="API Calls" class="w-6 h-6">
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">API Calls</p>
            <p class="text-3xl font-bold text-white">{{ integrationStats.apiCalls }}</p>
            <p class="text-sm text-emerald-400">This month</p>
          </div>
        </div>
      </div>

      <!-- Available Integrations -->
      <div class="px-8 mb-8">
        <h2 class="text-2xl font-bold text-white mb-6">Available Integrations</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div v-for="integration in availableIntegrations" :key="integration.id" class="integration-card">
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center space-x-3">
                <div class="w-12 h-12 shrink-0 bg-slate-700/50 rounded-xl flex items-center justify-center overflow-hidden">
                  <img :src="integrationIcon(integration.id || integration.icon)" :alt="integration.name" class="w-8 h-8 object-contain">
                </div>
                <div class="min-w-0">
                  <h3 class="text-lg font-semibold text-white truncate">{{ integration.name }}</h3>
                  <p class="text-sm text-slate-400">{{ integration.category }}</p>
                </div>
              </div>
              <div :class="integration.status === 'connected' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/50 text-slate-400'" class="px-3 py-1 rounded-full text-xs font-medium">
                {{ integration.status === 'connected' ? 'Connected' : 'Connect' }}
              </div>
            </div>
            
            <p class="text-slate-300 text-sm mb-4">{{ integration.description }}</p>
            
            <div class="space-y-2 mb-4">
              <div v-for="feature in integration.features" :key="feature" class="flex items-center text-sm text-slate-400">
                <svg class="w-4 h-4 text-emerald-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                </svg>
                {{ feature }}
              </div>
            </div>
            
            <button 
              @click="toggleIntegration(integration.id)"
              :class="integration.status === 'connected' ? 'btn-outline-modern' : 'btn-primary-modern'"
              class="w-full"
            >
              <span v-if="integration.status === 'connected'">Disconnect</span>
              <span v-else>Connect {{ integration.name }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Connected Integrations -->
      <div class="px-8 mb-8">
        <h2 class="text-2xl font-bold text-white mb-6">Connected Integrations</h2>
        <div class="bg-slate-800/40 backdrop-blur-sm rounded-3xl border border-slate-700/50 overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-slate-700/30">
                <tr>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-slate-300">Integration</th>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-slate-300">Status</th>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-slate-300">Last Sync</th>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-slate-300">Data Points</th>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-slate-300">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-700/50">
                <tr v-for="integration in connectedIntegrations" :key="integration.id" class="hover:bg-slate-700/20 transition-colors">
                  <td class="px-6 py-4">
                    <div class="flex items-center space-x-3">
                      <div class="w-10 h-10 shrink-0 bg-slate-700/50 rounded-xl flex items-center justify-center overflow-hidden">
                        <img :src="integrationIcon(integration.id || integration.icon)" :alt="integration.name" class="w-6 h-6 object-contain">
                      </div>
                      <div>
                        <p class="text-white font-medium">{{ integration.name }}</p>
                        <p class="text-sm text-slate-400">{{ integration.category }}</p>
                      </div>
                    </div>
                  </td>
                  <td class="px-6 py-4">
                    <span :class="getStatusClass(integration.status)" class="px-3 py-1 rounded-full text-xs font-medium">
                      {{ integration.status }}
                    </span>
                  </td>
                  <td class="px-6 py-4 text-sm text-slate-300">{{ formatDate(integration.lastSync) }}</td>
                  <td class="px-6 py-4 text-sm text-slate-300">{{ integration.dataPoints }}</td>
                  <td class="px-6 py-4">
                    <div class="flex space-x-2">
                      <button @click="syncIntegration(integration.id)" class="text-emerald-400 hover:text-emerald-300 text-sm font-medium">
                        Sync Now
                      </button>
                      <button @click="configureIntegration(integration.id)" class="text-cyan-400 hover:text-cyan-300 text-sm font-medium">
                        Configure
                      </button>
                      <button @click="disconnectIntegration(integration.id)" class="text-red-400 hover:text-red-300 text-sm font-medium">
                        Disconnect
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- API Documentation -->
      <div class="px-8 mb-8">
        <h2 class="text-2xl font-bold text-white mb-6">API Documentation</h2>
        <div class="bg-slate-800/40 backdrop-blur-sm rounded-3xl border border-slate-700/50 p-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 class="text-lg font-semibold text-white mb-4">Webhook Endpoints</h3>
              <div class="space-y-3">
                <div class="p-4 bg-slate-700/30 rounded-xl">
                  <p class="text-white font-medium mb-2">Timesheet Sync</p>
                  <p class="text-sm text-slate-400 font-mono">POST /api/webhooks/timesheet</p>
                </div>
                <div class="p-4 bg-slate-700/30 rounded-xl">
                  <p class="text-white font-medium mb-2">Payment Updates</p>
                  <p class="text-sm text-slate-400 font-mono">POST /api/webhooks/payment</p>
                </div>
                <div class="p-4 bg-slate-700/30 rounded-xl">
                  <p class="text-white font-medium mb-2">Job Notifications</p>
                  <p class="text-sm text-slate-400 font-mono">POST /api/webhooks/jobs</p>
                </div>
              </div>
            </div>
            <div>
              <h3 class="text-lg font-semibold text-white mb-4">Authentication</h3>
              <div class="space-y-3">
                <div class="p-4 bg-slate-700/30 rounded-xl">
                  <p class="text-white font-medium mb-2">API Key</p>
                  <p class="text-sm text-slate-400">Use your API key in the Authorization header</p>
                </div>
                <div class="p-4 bg-slate-700/30 rounded-xl">
                  <p class="text-white font-medium mb-2">Rate Limits</p>
                  <p class="text-sm text-slate-400">1000 requests per hour per integration</p>
                </div>
                <div class="p-4 bg-slate-700/30 rounded-xl">
                  <p class="text-white font-medium mb-2">Webhook Security</p>
                  <p class="text-sm text-slate-400">All webhooks are signed with HMAC-SHA256</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '../stores/userStore'
import AppLayout from '@/components/layout/AppLayout.vue'
import { integrationIcon } from '@/utils/integrationIcons'

// Store and Router
const userStore = useUserStore()
const router = useRouter()

// State
const isLoading = ref(false)

// Integration Statistics
const integrationStats = reactive({
  connected: 0,
  syncCount: 0,
  automations: 0,
  apiCalls: 0
})

// Available Integrations
const availableIntegrations = ref([
  {
    id: 'quickbooks',
    name: 'QuickBooks',
    category: 'Accounting',
    description: 'Sync your financial data and automate accounting workflows',
    icon: '/src/assets/images/icons/gold dollar coin.png',
    status: 'available',
    features: ['Invoice sync', 'Expense tracking', 'Financial reporting', 'Tax preparation']
  },
  {
    id: 'stripe',
    name: 'Stripe',
    category: 'Payments',
    description: 'Process payments and manage your payment workflows',
    icon: '/src/assets/images/icons/hand holding dollar coin.png',
    status: 'available',
    features: ['Payment processing', 'Subscription management', 'Fraud protection', 'Analytics']
  },
  {
    id: 'slack',
    name: 'Slack',
    category: 'Communication',
    description: 'Get notifications and updates in your Slack workspace',
    icon: '/src/assets/images/icons/blue chat bubble.png',
    status: 'available',
    features: ['Real-time notifications', 'Team collaboration', 'Custom alerts', 'Channel integration']
  },
  {
    id: 'google-calendar',
    name: 'Google Calendar',
    category: 'Scheduling',
    description: 'Sync your work schedule and manage appointments',
    icon: '/src/assets/images/icons/red calendar.png',
    status: 'available',
    features: ['Schedule sync', 'Event management', 'Reminder integration', 'Team coordination']
  },
  {
    id: 'dropbox',
    name: 'Dropbox',
    category: 'Storage',
    description: 'Store and sync your documents and files',
    icon: '/src/assets/images/icons/books.png',
    status: 'available',
    features: ['File sync', 'Document storage', 'Version control', 'Team sharing']
  },
  {
    id: 'zapier',
    name: 'Zapier',
    category: 'Automation',
    description: 'Create automated workflows between your apps',
    icon: '/src/assets/images/icons/ai-powered financial research tools.png',
    status: 'available',
    features: ['Workflow automation', 'App connections', 'Custom triggers', 'Data transformation']
  }
])

// Connected Integrations
const connectedIntegrations = ref([
  {
    id: 'quickbooks',
    name: 'QuickBooks',
    category: 'Accounting',
    icon: '/src/assets/images/icons/gold dollar coin.png',
    status: 'connected',
    lastSync: '2024-12-01T10:30:00Z',
    dataPoints: '1,247'
  },
  {
    id: 'stripe',
    name: 'Stripe',
    category: 'Payments',
    icon: '/src/assets/images/icons/hand holding dollar coin.png',
    status: 'connected',
    lastSync: '2024-12-01T09:15:00Z',
    dataPoints: '856'
  }
])

// Computed user properties
const currentUser = computed(() => userStore.currentUser)

// Methods
const fetchIntegrationData = async () => {
  try {
    isLoading.value = true
    
    // Fetch integration statistics
    const statsResponse = await fetch('/api/integrations/stats', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (statsResponse.ok) {
      const stats = await statsResponse.json()
      Object.assign(integrationStats, stats)
    }

    // Fetch available integrations
    const availableResponse = await fetch('/api/integrations/available', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (availableResponse.ok) {
      const available = await availableResponse.json()
      availableIntegrations.value = available
    }

    // Fetch connected integrations
    const connectedResponse = await fetch('/api/integrations/connected', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (connectedResponse.ok) {
      const connected = await connectedResponse.json()
      connectedIntegrations.value = connected
    }

  } catch (error) {
    console.error('Error fetching integration data:', error)
    // Load mock data for demo
    loadMockData()
  } finally {
    isLoading.value = false
  }
}

const loadMockData = () => {
  // Mock integration statistics
  Object.assign(integrationStats, {
    connected: 2,
    syncCount: 45,
    automations: 8,
    apiCalls: 1247
  })

  // Update integration statuses based on connected integrations
  availableIntegrations.value.forEach(integration => {
    const isConnected = connectedIntegrations.value.some(connected => connected.id === integration.id)
    integration.status = isConnected ? 'connected' : 'available'
  })
}

const toggleIntegration = async (integrationId: string) => {
  try {
    const integration = availableIntegrations.value.find(i => i.id === integrationId)
    if (!integration) return

    if (integration.status === 'connected') {
      // Disconnect integration
      await disconnectIntegration(integrationId)
    } else {
      // Connect integration
      await connectIntegration(integrationId)
    }
  } catch (error) {
    console.error('Error toggling integration:', error)
  }
}

const connectIntegration = async (integrationId: string) => {
  try {
    const response = await fetch(`/api/integrations/${integrationId}/connect`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      // Update integration status
      const integration = availableIntegrations.value.find(i => i.id === integrationId)
      if (integration) {
        integration.status = 'connected'
      }
      
      // Add to connected integrations
      const newConnected = {
        id: integrationId,
        name: integration?.name || 'Unknown',
        category: integration?.category || 'Unknown',
        icon: integration?.icon || '',
        status: 'connected',
        lastSync: new Date().toISOString(),
        dataPoints: '0'
      }
      connectedIntegrations.value.push(newConnected)
      
      // Update stats
      integrationStats.connected++
    }
  } catch (error) {
    console.error('Error connecting integration:', error)
  }
}

const disconnectIntegration = async (integrationId: string) => {
  try {
    const response = await fetch(`/api/integrations/${integrationId}/disconnect`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      // Update integration status
      const integration = availableIntegrations.value.find(i => i.id === integrationId)
      if (integration) {
        integration.status = 'available'
      }
      
      // Remove from connected integrations
      connectedIntegrations.value = connectedIntegrations.value.filter(i => i.id !== integrationId)
      
      // Update stats
      integrationStats.connected--
    }
  } catch (error) {
    console.error('Error disconnecting integration:', error)
  }
}

const syncIntegration = async (integrationId: string) => {
  try {
    const response = await fetch(`/api/integrations/${integrationId}/sync`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      // Update last sync time
      const integration = connectedIntegrations.value.find(i => i.id === integrationId)
      if (integration) {
        integration.lastSync = new Date().toISOString()
      }
      
      // Update sync count
      integrationStats.syncCount++
    }
  } catch (error) {
    console.error('Error syncing integration:', error)
  }
}

const configureIntegration = (integrationId: string) => {
  // Navigate to integration configuration page
  router.push(`/integrations/${integrationId}/configure`)
}

const getStatusClass = (status: string) => {
  switch (status.toLowerCase()) {
    case 'connected':
      return 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
    case 'syncing':
      return 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
    case 'error':
      return 'bg-red-500/20 text-red-400 border border-red-500/30'
    default:
      return 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
  }
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Lifecycle
onMounted(() => {
  fetchIntegrationData()
})
</script>

<style scoped>
.integration-stat-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-3xl border border-slate-700/50 p-6 transition-all duration-200 hover:bg-slate-700/40 hover:border-slate-600/50;
}

.integration-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-3xl border border-slate-700/50 p-6 transition-all duration-200 hover:bg-slate-700/40 hover:border-slate-600/50;
}

.btn-primary-modern {
  @apply inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-medium rounded-xl hover:from-emerald-600 hover:to-cyan-600 transition-all duration-200 shadow-lg shadow-emerald-500/25;
}

.btn-outline-modern {
  @apply inline-flex items-center px-4 py-2 border border-slate-600 text-slate-300 font-medium rounded-xl hover:bg-slate-700/50 hover:border-slate-500 transition-all duration-200;
}
</style>
